import { useEffect, useState, type FormEvent } from "react";
import { api, type User } from "../lib/api";

interface StorageCamera {
  cameraId: string;
  cameraName: string;
  bytes: number;
  segments: number;
  lockedBytes: number;
}

interface StorageData {
  path: string;
  totalBytes: number;
  usedBytes: number;
  availableBytes: number;
  usagePercent: number;
  recordingBytes: number;
  segmentCount: number;
  lockedSegmentCount: number;
  policy: {
    minimumFreeBytes: number;
    maximumUsagePercent: number;
    targetUsagePercent: number;
    retentionDays: number;
  };
  cameras: StorageCamera[];
}

interface DeleteResult {
  deletedFiles: number;
  deletedBytes: number;
  deletedSegments: number;
  skippedFiles: number;
  failedFiles: number;
}

export function StoragePage({ user }: { user: User }) {
  const [data, setData] = useState<StorageData | null>(null);
  const [message, setMessage] = useState("");
  const [deletingCameraId, setDeletingCameraId] = useState<string | null>(null);
  const canDeleteRecordings = user.permissions.includes("recording.delete");
  const load = () => api<StorageData>("/api/storage").then(setData);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, []);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/storage/policy", {
        method: "PUT",
        body: JSON.stringify({
          minimumFreeBytes: Number(form.get("minimumFreeGiB")) * 1024 ** 3,
          maximumUsagePercent: Number(form.get("maximumUsagePercent")),
          targetUsagePercent: Number(form.get("targetUsagePercent")),
          retentionDays: Number(form.get("retentionDays"))
        })
      });
      setMessage("저장소 정책을 저장했습니다.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "저장 실패");
    }
  };

  const emptyCameraStorage = async (camera: StorageCamera) => {
    const lockedNotice = camera.lockedBytes > 0
      ? `\n삭제 방지 영상 ${formatBytes(camera.lockedBytes)}도 함께 삭제됩니다.`
      : "";
    if (!window.confirm(
      `${camera.cameraName}의 녹화 영상 ${camera.segments.toLocaleString()}개 (${formatBytes(camera.bytes)})를 모두 삭제하시겠습니까?`+
      `${lockedNotice}\n이 작업은 되돌릴 수 없습니다.`
    )) return;
    setDeletingCameraId(camera.cameraId);
    setMessage(`${camera.cameraName} 녹화 영상을 삭제하는 중입니다…`);
    try {
      const result = await api<DeleteResult>(`/api/storage/cameras/${camera.cameraId}/recordings`, { method: "DELETE" });
      const skipped = result.skippedFiles > 0
        ? ` 현재 녹화 중인 파일 ${result.skippedFiles.toLocaleString()}개는 유지했습니다.`
        : "";
      const failed = result.failedFiles > 0
        ? ` 삭제하지 못한 파일 ${result.failedFiles.toLocaleString()}개가 있습니다.`
        : "";
      setMessage(`${camera.cameraName} 녹화 파일 ${result.deletedFiles.toLocaleString()}개 (${formatBytes(result.deletedBytes)})를 삭제했습니다.${skipped}${failed}`);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "녹화 영상 삭제 실패");
    } finally {
      setDeletingCameraId(null);
    }
  };

  if (!data) return <div className="loading-inline">저장소 정보를 불러오는 중…</div>;
  return <>
    <div className="page-heading">
      <div><p className="eyebrow">녹화 저장 공간</p><h2>저장소</h2></div>
      <span className="text-secondary">{data.path}</span>
    </div>
    {message && <div className="alert alert-secondary py-2">{message}</div>}
    <div className="row g-3">
      {[["전체", data.totalBytes], ["사용", data.usedBytes], ["남음", data.availableBytes], ["녹화 데이터", data.recordingBytes]].map(([label, value]) =>
        <div className="col-6 col-xl-3" key={String(label)}><div className="metric-card"><i className="bi bi-device-hdd text-info"/><strong>{formatBytes(Number(value))}</strong><span>{label}</span></div></div>
      )}
    </div>
    <div className="panel mt-4">
      <div className="panel-title"><h3>사용률</h3><span>{data.usagePercent.toFixed(1)}%</span></div>
      <div className="progress storage-progress"><div className={`progress-bar ${data.usagePercent >= data.policy.maximumUsagePercent ? "bg-danger" : "bg-primary"}`} style={{ width: `${data.usagePercent}%` }}/></div>
      <div className="small text-secondary mt-2">녹화 파일 {data.segmentCount.toLocaleString()}개 · 삭제 방지 {data.lockedSegmentCount.toLocaleString()}개</div>
    </div>
    <div className="row g-4 mt-1">
      <div className="col-xl-7"><div className="panel">
        <div className="panel-title"><h3>카메라별 사용량</h3></div>
        <div className="table-responsive"><table className="table table-dark align-middle mb-0">
          <thead><tr><th>카메라</th><th>녹화 파일</th><th>사용량</th><th>삭제 방지</th>{canDeleteRecordings && <th className="text-end">관리</th>}</tr></thead>
          <tbody>{data.cameras.map((camera) => <tr key={camera.cameraId}>
            <td>{camera.cameraName}</td>
            <td>{camera.segments.toLocaleString()}</td>
            <td>{formatBytes(camera.bytes)}</td>
            <td>{formatBytes(camera.lockedBytes)}</td>
            {canDeleteRecordings && <td className="text-end"><button
              type="button"
              className="btn btn-sm btn-outline-danger"
              disabled={deletingCameraId !== null || camera.segments === 0}
              onClick={() => void emptyCameraStorage(camera)}
            >{deletingCameraId === camera.cameraId ? "삭제 중…" : "비우기"}</button></td>}
          </tr>)}</tbody>
        </table></div>
        {canDeleteRecordings && <p className="small text-secondary mt-3 mb-0">녹화 중인 카메라는 현재 작성 중인 파일과 이후 생성되는 녹화 영상이 유지됩니다.</p>}
      </div></div>
      <div className="col-xl-5"><form className="panel" onSubmit={save}>
        <div className="panel-title"><h3>자동 삭제 정책</h3></div>
        <label>전체 보관 기간 (일, 0은 무제한)</label><input className="form-control" name="retentionDays" type="number" min="0" max="3650" step="1" defaultValue={data.policy.retentionDays}/>
        <label>최소 남은 용량 (GiB)</label><input className="form-control" name="minimumFreeGiB" type="number" min="0" step="1" defaultValue={(data.policy.minimumFreeBytes / 1024 ** 3).toFixed(0)}/>
        <label>삭제 시작 사용률 (%)</label><input className="form-control" name="maximumUsagePercent" type="number" min="50" max="99.9" step="0.1" defaultValue={data.policy.maximumUsagePercent}/>
        <label>삭제 종료 사용률 (%)</label><input className="form-control" name="targetUsagePercent" type="number" min="40" max="99" step="0.1" defaultValue={data.policy.targetUsagePercent}/>
        <div className="d-flex gap-2 mt-3"><button className="btn btn-primary flex-fill">저장</button><button type="button" className="btn btn-outline-warning" onClick={() => void api("/api/storage/cleanup", { method: "POST" }).then(() => setMessage("정리 작업을 요청했습니다."))}>지금 정리</button></div>
      </form></div>
    </div>
  </>;
}

function formatBytes(value: number) {
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let size = value, index = 0;
  while (size >= 1024 && index < units.length - 1) { size /= 1024; index += 1; }
  return `${size.toFixed(index > 2 ? 2 : index ? 1 : 0)} ${units[index]}`;
}
